import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import {
  RESET_CUSTOMER_PROFILE,
  setCustomerId,
  setPetId,
} from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { useEffect, useRef } from "react";
import { useParams } from "react-router";
import { useSearchParams } from "react-router-dom";
import { useCustomerDetailsAPI } from "./api_hooks/customer_profile/useCustomerDetailsAPI";
import { useCustomerPetListAPI } from "./api_hooks/customer_profile/useCustomerPetListAPI";

export const useVMSParams = () => {
  const [URLSearchParams, _setParams] = useSearchParams();
  const params = useParams();
  const dispatch = useAppDispatch();
  const fetchCustomerDetails = useCustomerDetailsAPI();
  const fetchAndSetCustomerPetDetails = useCustomerPetListAPI();

  const customerIdFromURL = params.user_id ?? null;
  const petIdFromURL = params.pet_id || URLSearchParams.get("pet_id") || null;

  /* Read through a ref so switching customer does not re-trigger the effect
     that just wrote the new customer into the store. */
  const storedCustomerId = useAppSelector((state) => state.routes.auth.CUSTOMER_PROFILE.state.customerId);
  const storedCustomerIdRef = useRef(storedCustomerId);
  storedCustomerIdRef.current = storedCustomerId;

  /* Identity of the customer/pet this hook has already loaded. Keyed rather
     than a plain boolean so opening a second customer without unmounting the
     page (PWL -> customer A -> customer B) still refetches. */
  const loadedKeyRef = useRef<string | null>(null);

  const isNewUser = () =>
    URLSearchParams.get("new_user") === "true" ||
    URLSearchParams.get("from_om") === "true" ||
    URLSearchParams.get("from_cart") === "true";

  const doSeriesCall = async () => {
    /* The URL addresses a different customer than the one sitting in the
       store - drop that customer's pets, EMRs, invoices and vaccination data
       before anything is fetched, so no tab can render the previous patient. */
    if (storedCustomerIdRef.current && storedCustomerIdRef.current !== customerIdFromURL) {
      dispatch(RESET_CUSTOMER_PROFILE());
    }

    if (petIdFromURL) {
      dispatch(setPetId({ petId: petIdFromURL }));
    }

    dispatch(setCustomerId({ customerId: customerIdFromURL }));
    await fetchCustomerDetails(customerIdFromURL);

    const asyncCalls: Array<Promise<any>> = [];

    if (petIdFromURL) {
      asyncCalls.push(
        fetchAndSetCustomerPetDetails(customerIdFromURL, {
          petId: petIdFromURL,
        }),
      );
    } else if (isNewUser()) {
      asyncCalls.push(
        fetchAndSetCustomerPetDetails(customerIdFromURL, {
          selectFirstPet: true,
        }),
      );
    }

    await Promise.all(asyncCalls);
  };

  useEffect(() => {
    /* Routes that reuse the already selected customer (cart, slots) reach here
       without a user_id - leave the store untouched for them. */
    if (!customerIdFromURL) {
      if (petIdFromURL) {
        dispatch(setPetId({ petId: petIdFromURL }));
      }
      return;
    }

    const requestedKey = `${customerIdFromURL}:${petIdFromURL ?? ""}`;
    if (loadedKeyRef.current === requestedKey) return;
    loadedKeyRef.current = requestedKey;

    doSeriesCall();
  }, [customerIdFromURL, petIdFromURL]);
};
